import React from "react"
import styled, { css } from "styled-components"
import { up, down } from "styled-breakpoints"
import colors from "@constants/colors"
import { timings, cubicBezier } from "@constants/animation"
import { subtext } from "@constants/typography"
import { MobileOnly, LaptopUpOnly } from "@utils/MediaQueries"

const Preview = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  display: none;
  width: 180px;
  height: 240px;
  padding: 16px;
  opacity: 0;
  transform: translate(-70px, -260px);
  transition: opacity ${timings.medium} ${cubicBezier};
  will-change: opacity;
  border: solid 1px ${colors.divider};
  box-shadow: 0px 2px 40px rgba(0, 0, 0, 0.1);
  background-color: ${colors.snow};
  pointer-events: none;
  cursor: default;

  ${up("laptop")} {
    display: flex;
  }
`

const selectedProps = css`
  border: solid 2px ${colors.obsidian};
`
const Container = styled.div<{ selected?: boolean }>`
  ${({ selected }) => css`
    position: relative;
    width: 36px;
    height: 36px;
    margin-right: 16px;
    margin-bottom: 14px;
    padding: 2px;
    outline: none;
    border: none;
    border-radius: 4px;

    ${up("tablet")} {
      width: 40px;
      height: 40px;

      &:hover {
        ${Preview} {
          opacity: 1;
          transition: opacity ${timings.quick} ${cubicBezier}
          pointer-events: initial;
        }
      }
    }
    
    ${down("tablet")} {
      width: 100%;
      height: auto;
    }

    ${selected ? selectedProps : ""}
  `}
`

const OptionButton = styled.button<{
  isColor?: boolean
  imageSrc?: string
  optionUnavailable?: boolean
}>`
  ${({ isColor, imageSrc, optionUnavailable }) => css`
    opacity: ${optionUnavailable ? 0.2 : 1};
    width: 100%;
    height: 100%;
    outline: none;
    border: none;
    border-radius: 2px;
    background-color: ${imageSrc || isColor
      ? "transparent"
      : colors.greyCheckbox};
    background-image: url(${imageSrc});
    background-position: center center;
    background-repeat: no-repeat;
    background-size: 100%;

    &:hover {
      cursor: pointer;
    }
  `}
`
const OptionWrapper = styled.div<{
  optionUnavailable?: boolean
}>`
  ${({ optionUnavailable }) => css`
    display: flex;
    gap: 20px;
    align-items: center;
    padding: 10px;
    opacity: ${optionUnavailable ? "0.3" : "1"};

    [class*="Option__Subtext"] {
      text-align: left;
    }

    img {
      width: 80px;
      height: 80px;
      object-fit: contain;
    }
  `}
`

const Subtext = styled.div`
  ${subtext};
  text-align: center;
  color: ${colors.obsidian};

  ${down("laptop")} {
    font-size: 14px;
  }
`

const Image = styled.img`
  width: 148px;
  height: 148px;
  object-fit: contain;
  object-position: center;
`

const Diamond = styled.div`
  display: none;

  ${up("laptop")} {
    position: absolute;
    left: calc(50% - 8px);
    bottom: -8px;
    display: initial;
    transform: rotate(-45deg);
    width: 16px;
    height: 16px;
    border-left: solid 1px ${colors.divider};
    border-bottom: solid 1px ${colors.divider};
    background-color: ${colors.snow};
  }
`

interface OptionProps {
  imageSrc: string
  selected: boolean
  optionType: string
  optionValue: string
  optionUnavailable?: boolean
  onSelectOption: (optionType, optionValue) => void
}

const Option: React.FC<OptionProps> = ({
  imageSrc,
  selected,
  optionType,
  optionValue,
  optionUnavailable,
  onSelectOption,
}) => {
  return (
    <Container selected={selected}>
      <MobileOnly>
        <OptionWrapper
          optionUnavailable={optionUnavailable}
          onClick={() => {
            onSelectOption(optionType, optionValue)
          }}
        >
          <Image src={imageSrc} alt={optionValue} />
          <Subtext>{optionValue}</Subtext>
        </OptionWrapper>
      </MobileOnly>
      <LaptopUpOnly>
        <OptionButton
          imageSrc={imageSrc}
          optionUnavailable={optionUnavailable}
          onClick={() => {
            onSelectOption(optionType, optionValue)
          }}
        />
        <Preview>
          <Diamond />
          <Image src={imageSrc} alt={optionValue} />
          <Subtext>{optionValue}</Subtext>
        </Preview>
      </LaptopUpOnly>
    </Container>
  )
}

export default Option
